import {
  CheckboxField,
  DatePickerField,
  InputField,
  SelectField,
} from "@/features/FormCard/Fields";
import { Label } from "@radix-ui/react-label";
import React, { useMemo } from "react";
import { Control } from "react-hook-form";
import { z } from "zod";
import { TSections } from "./Fields";
import { Divider } from "@heroui/divider";
import { useCleanWhenHidden } from "@/hooks/useCleanWhenHidden";
import { useTranslation } from "react-i18next";

type TSelectOption = Record<
  string,
  ReadonlyArray<{
    value: string;
  }>
>;

export type TDynamicSectionFields = {
  [fieldName: string]: {
    label: string;
    validator: z.ZodTypeAny;
    field?: (control: Control<any>) => React.ReactNode;
  };
};

/**
 * Props for the DynamicFields component
 */
export type TDynamicFieldsProps = {
  control: Control<any>;
  fieldValue: string;
  primaryValue?: string;
  isLoadingTranslations: boolean;
  config?: TDynamicFieldConfig;
  options?: TSelectOption;
};

/**
 * Props for the DynamicFields component
 */
export type TConditionalAdditionalFields = {
  control: Control<any>;
  fieldKey: string;
  isLoadingTranslations: boolean;
  fieldsToDisplayByKey: Record<
    string,
    Record<string, { label: string; validator: z.ZodTypeAny }>
  >;
};

/**
 * Configuration for dynamic fields
 */
export type TDynamicFieldConfig = {
  select_field_option: TSections;
  select: {
    [primaryValue: string]: {
      [secondaryValue: string]: string | boolean;
    };
  };
  section: {
    [sectionName: string]: TDynamicSectionFields;
  };
};

/**
 * Unwraps a Zod schema by traversing through any optional or nullable wrappers,
 * and returns the underlying type name.
 */
function getZodTypeName(validator: z.ZodTypeAny): string {
  let schema: z.ZodTypeAny = validator;
  while (true) {
    const def = (schema as any)?._def;
    if (!def) break;
    if (def.innerType) {
      schema = def.innerType as z.ZodTypeAny;
      continue;
    }
    if (def.schema) {
      schema = def.schema as z.ZodTypeAny;
      continue;
    }
    break;
  }

  if (schema instanceof z.ZodBoolean) return "ZodBoolean";
  if (schema instanceof z.ZodDate) return "ZodDate";
  if (schema instanceof z.ZodNumber) return "ZodNumber";
  if (schema instanceof z.ZodString) return "ZodString";
  if (schema instanceof z.ZodEnum) return "ZodEnum";
  return "ZodUnknown";
}

/**
 * Creates a checkbox field component
 */
function createCheckboxField(
  fieldName: string,
  fieldConfig: {
    label: string;
    validator: z.ZodTypeAny;
  },
  control: Control<any>,
): React.ReactElement {
  return (
    <div
      key={fieldName}
      className="flex h-full items-end md:justify-start pr-2"
    >
      <CheckboxField
        name={fieldName}
        label={fieldConfig.label}
        control={control}
      />
    </div>
  );
}

/**
 * Creates a date picker field component
 */
function createDatePickerField(
  fieldName: string,
  fieldConfig: {
    label: string;
    validator: z.ZodTypeAny;
  },
  control: Control<any>,
): React.ReactElement {
  return (
    <DatePickerField
      key={fieldName}
      name={fieldName}
      label={fieldConfig.label}
      control={control}
      isRequired={false}
    />
  );
}

/**
 * Creates a select field component
 */
function createSelectField(
  fieldName: string,
  fieldConfig: {
    label: string;
    validator: z.ZodTypeAny;
  },
  options: TSelectOption,
  control: Control<any>,
): React.ReactElement {
  return (
    <SelectField
      key={fieldName}
      name={fieldName}
      label={fieldConfig.label}
      control={control}
      options={options[fieldName].map((option) => ({
        value: option.value,
        label: option.value,
      }))}
    />
  );
}

/**
 * Creates an input field component
 */
function createInputField(
  fieldName: string,
  fieldConfig: {
    label: string;
    validator: z.ZodTypeAny;
  },
  control: Control<any>,
  type: "text" | "number" = "text",
): React.ReactElement {
  return (
    <InputField
      type={type}
      key={fieldName}
      name={fieldName}
      label={fieldConfig.label}
      control={control}
      isRequired={false}
    />
  );
}

/**
 * Creates the appropriate field component based on the field type
 */
function createField({
  fieldName,
  fieldConfig,
  control,
  options,
}: {
  fieldName: string;
  fieldConfig: {
    label: string;
    validator: z.ZodTypeAny;
    field?: (control: Control<any>) => React.ReactNode;
  };
  control: Control<any>;
  options?: TSelectOption;
}): React.ReactElement {
  // If a custom field component is provided, use it
  if (fieldConfig.field) {
    return <div key={fieldName}>{fieldConfig.field(control)}</div>;
  }

  const typeName = getZodTypeName(fieldConfig.validator);

  if (options && options[fieldName]) {
    return createSelectField(fieldName, fieldConfig, options, control);
  }

  // Create a checkbox field for boolean types
  if (typeName === "ZodBoolean") {
    return createCheckboxField(fieldName, fieldConfig, control);
  }

  // Create a date picker field for date types
  // Note: Using metadata instead of description for more explicit type identification
  if (typeName === "ZodDate" || fieldConfig.validator.description === "date") {
    return createDatePickerField(fieldName, fieldConfig, control);
  }

  return createInputField(
    fieldName,
    fieldConfig,
    control,
    typeName === "ZodNumber" ? "number" : "text",
  );
}

function useFieldElements(
  sectionFields: TDynamicSectionFields | null,
  control: Control<any>,
  options?: TSelectOption,
) {
  return useMemo(() => {
    if (!sectionFields) return null;

    return Object.entries(sectionFields).map(([fieldName, fieldConfig]) => {
      return createField({ fieldName, fieldConfig, control, options });
    });
  }, [sectionFields, control, options]);
}

function DetailedInformationFields({
  fields,
}: {
  fields: React.ReactElement[] | null;
}) {
  const { t } = useTranslation();

  if (!fields?.length) {
    return null;
  }

  return (
    <>
      <Divider />
      <Label>
        {t("sections.detailed_information", {
          defaultValue: "Detailed information",
        })}
      </Label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{fields}</div>
    </>
  );
}

export function ConditionalAdditionalFields({
  fieldKey,
  control,
  isLoadingTranslations,
  fieldsToDisplayByKey,
}: TConditionalAdditionalFields) {
  const sectionFields = useMemo(() => {
    if (!fieldKey || !(fieldKey in fieldsToDisplayByKey)) {
      return null;
    }
    return fieldsToDisplayByKey[fieldKey];
  }, [fieldKey, fieldsToDisplayByKey, isLoadingTranslations]);

  useCleanWhenHidden(sectionFields);

  const fieldsJSX = useFieldElements(sectionFields, control);

  if (isLoadingTranslations || !sectionFields) {
    return null;
  }

  return <DetailedInformationFields fields={fieldsJSX} />;
}

/**
 * DynamicFields Component
 *
 * Renders dynamic form fields based on a configuration.
 * It determines which section of fields to render based on the provided fieldValue and primaryValue.
 *
 * @param {TDynamicFieldsProps} props - The props for the component.
 * @returns {JSX.Element|null} The rendered dynamic fields, or null if no section is found or if translations are loading.
 */
export function DynamicFields({
  fieldValue,
  primaryValue,
  control,
  isLoadingTranslations,
  config,
  options,
}: TDynamicFieldsProps) {
  const sectionFields = useMemo(() => {
    if (isLoadingTranslations || !config || !primaryValue || !fieldValue) {
      return null;
    }

    let sectionKey = null;

    for (const section of config.select_field_option) {
      if (section.key === primaryValue) {
        const matchingItem = section.items.find(
          (item) => item.value === fieldValue,
        );

        if (matchingItem) {
          const sectionKeyValue = section.key;
          const itemValue = matchingItem.value;

          if (
            config.select[sectionKeyValue] &&
            config.select[sectionKeyValue][itemValue]
          ) {
            const potentialSectionKey =
              config.select[sectionKeyValue][itemValue];

            if (
              typeof potentialSectionKey === "string" &&
              config.section[potentialSectionKey]
            ) {
              sectionKey = potentialSectionKey;
              break;
            }
          }
        }
      }
    }

    if (!sectionKey || !config.section[sectionKey]) {
      return null;
    }

    return config.section[sectionKey];
  }, [config, fieldValue, isLoadingTranslations, primaryValue]);

  useCleanWhenHidden(sectionFields);

  const fieldsJSX = useFieldElements(sectionFields, control, options);

  if (isLoadingTranslations || !config || !sectionFields) {
    return null;
  }

  return <DetailedInformationFields fields={fieldsJSX} />;
}

export default DynamicFields;
